import React, { ReactNode, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, ChevronDown, X } from "lucide-react";
import { Link } from "wouter";
// import logo from '@/assets/staymiddle-logo.png';
import logo from '@/assets/staymiddle-logo-white.png';
import logoIcon from '@/assets/staymiddle-logo-icon-white.png';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactElement<{ className?: string }>;
  active?: boolean;
}

export function Sidebar({ navItems, collapsed = false, setCollapsed }: { navItems: NavItem[]; collapsed?: boolean; setCollapsed?: (collapsed: boolean) => void; }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Listen for custom event to open sidebar from header hamburger menu
  React.useEffect(() => {
    const handler = () => setSidebarOpen(true);
    window.addEventListener('open-sidebar', handler);
    return () => window.removeEventListener('open-sidebar', handler);
  }, []);

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`hidden md:flex flex-col fixed top-0 left-0 h-screen ${collapsed ? 'w-16' : 'w-64'} p-4 border-r transition-all duration-200 bg-primary font-semibold z-30`}>
        <div className={`flex ${collapsed ? 'flex-col items-center gap-2' : 'flex-row items-center justify-between'} mb-8`}>
          <Link href="/">
            <img
              src={collapsed ? logoIcon : logo}
              alt="StayMiddle Logo"
              className={`cursor-pointer transition-opacity hover:opacity-80 ${collapsed ? 'h-10 w-auto' : 'h-10 w-auto'}`}
            />
          </Link>
          {setCollapsed && (
            <button
              className={`${collapsed ? 'mt-2' : 'ml-2'} p-1 rounded hover:bg-accent`}
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
              {collapsed ? <ChevronDown className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
        <div className="space-y-4 flex-1">
          <TooltipProvider>
            {navItems.map((item) => (
              <Link href={item.href} key={item.href}>
                {collapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={`flex items-center p-1 my-2 rounded-md cursor-pointer font-semibold justify-center ${
                          item.active
                            ? "bg-accent text-accent-foreground"
                            : "hover:bg-accent"
                        }`}
                        tabIndex={0}
                      >
                        {React.cloneElement(item.icon, {
                          className: [item.icon.props.className, 'w-10 h-10'].filter(Boolean).join(' ')
                        })}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="text-sm font-semibold">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div
                    className={`flex items-center p-2 py-3 my-2 rounded-md cursor-pointer font-semibold ${
                      item.active
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent"
                    }`}
                  >
                    {React.cloneElement(item.icon, {
                      className: [item.icon.props.className, 'w-6 h-6 mr-2'].filter(Boolean).join(' ')
                    })}
                    <span>{item.label}</span>
                  </div>
                )}
              </Link>
            ))}
          </TooltipProvider>
        </div>
      </div>
      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        {/* SheetTrigger is hidden, but present to allow imperative opening */}
        <SheetTrigger asChild>
          <span className="hidden" />
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-4 bg-primary">
          <div className="mb-6 flex items-center justify-center">
            <Link href="/">
              <img
                src={collapsed ? logoIcon : logo}
                alt="StayMiddle Logo"
                className={`cursor-pointer transition-opacity hover:opacity-80 ${collapsed ? 'h-10 w-10' : 'h-10 w-auto'}`}
              />
            </Link>
          </div>
          <div className="space-y-2">
            <TooltipProvider>
              {navItems.map((item) => (
                <Link href={item.href} key={item.href}>
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`flex items-center p-1 my-2 rounded-md cursor-pointer font-semibold justify-center ${
                            item.active
                              ? "bg-accent text-accent-foreground"
                              : "hover:bg-accent"
                          }`}
                          tabIndex={0}
                        >
                          {React.cloneElement(item.icon, {
                            className: [item.icon.props.className, 'w-10 h-10'].filter(Boolean).join(' ')
                          })}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="text-sm font-semibold">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <div
                      className={`flex items-center p-2 py-3 my-2 rounded-md cursor-pointer font-semibold ${
                        item.active
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent"
                      }`}
                    >
                      {React.cloneElement(item.icon, {
                        className: [item.icon.props.className, 'w-6 h-6 mr-2'].filter(Boolean).join(' ')
                      })}
                      <span>{item.label}</span>
                    </div>
                  )}
                </Link>
              ))}
            </TooltipProvider>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
} 